import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createBrowserRouter } from 'react-router';
import { ThemeProvider } from '@mui/material/styles';
import { routes } from './app/app.routes';
import { firstPaintReady, restore, startSessionRecovery } from './app/core/session/session.store';
import { directClient, setAppClient } from './app/core/api/http';
import { createRenewingClient } from './app/core/http/renewing-client';
import { createOfflineGuardClient } from './app/core/http/offline-guard';
import { createStoringClient } from './app/core/stored-views/storing-client';
import { createObservingClient } from './app/core/reach/observing-client';
import { startFetchAhead } from './app/core/stored-views/fetch-ahead';
import { startOutboxWatch } from './app/core/outbox/outbox.store';
import { noServiceWorker, registerServiceWorker } from './app/core/pwa/service-worker';
import { startUpdateWatch } from './app/core/pwa/update.store';
import { environment } from './environments/environment';
import { theme } from './app/core/theme/theme';

/**
 * The global stylesheet and the fonts, which `angular.json` used to list in its `styles` array.
 * They are imports now because Vite has no equivalent of that array — the entry module is the
 * only place a stylesheet can be declared once for the whole application.
 *
 * Order matters: the fonts must be registered before `styles.scss`, which names them.
 */
import '@fontsource-variable/archivo/index.css';
import '@fontsource/ibm-plex-sans/400.css';
import '@fontsource/ibm-plex-sans/500.css';
import '@fontsource/ibm-plex-sans/600.css';
import '@fontsource/ibm-plex-mono/400.css';
import '@fontsource/material-icons/index.css';
import './styles.scss';

/**
 * 034 T002 — the application entry, replacing `bootstrapApplication(App, appConfig)`.
 *
 * **This renders a placeholder, and that is the correct state for Phase 1.** The toolchain is what
 * this phase delivers; the shell, the router and the session arrive in Phase 2 (T015–T022), and
 * until T022 the end-to-end suite reports a number that is noise rather than progress. Phase 1's
 * gate is only that the build, the unit runner and the linter work and are each proven able to
 * fail (T007).
 *
 * `StrictMode` is on from the first commit rather than added later. It double-invokes effects in
 * development, which surfaces the class of bug that a port introduces most easily — an effect that
 * is not idempotent — at the moment the code is written rather than months afterwards.
 */
/**
 * Install the wrappers over the direct client, once, before anything can issue a request. This is
 * what Angular did by REGISTERING `authInterceptor` and `offlineInterceptor` in `app.config.ts`.
 * Session traffic is unaffected — it asks for `directClient()` by name, which is the whole point of
 * the split.
 *
 * 036 (issue 208, research R-008) — `core/http/offline-client.ts` is gone: FR-003 forbids deciding
 * "the server cannot be reached" from `navigator.onLine` alone, which was that wrapper's entire
 * mechanism. `createStoringClient` takes its place in the composition, doing a different job — a
 * `GET` still reaches the network and is judged on what actually happens to it (R-006's time limit,
 * a real refusal), and only THEN, if it truly cannot be answered, does a stored answer stand in.
 *
 * **Storing OUTSIDE renewing** (a request reaching it must already carry its token and have had its
 * one 401 retry, or a stale-token refusal would be stored/served as though the SERVER could not be
 * reached) **and OUTSIDE observing too — a departure from this slice's brief, which asked for
 * `observing(storing(renewing(…)))`, recorded here rather than silently overridden.** With observing
 * OUTSIDE storing, a `GET` storing falls back for is a RESOLVED promise by the time observing sees
 * it — `core/reach/observing-client.ts` reports every settled promise as `noteOutcome(200)`, so
 * `core/reach/`'s state would read `reachable` for every read a stored view happens to cover, even
 * with the network completely gone. That is precisely the state SC-022's connection line and FR-005
 * key on, so home would show NEITHER while serving five-for-five from storage — the opposite of what
 * FR-024's stored-list line and FR-005's connection line are both supposed to say at once. Putting
 * observing BETWEEN storing and renewing instead means it always sees the REQUEST'S OWN true
 * outcome — unreachable, failing, or genuinely answered — before storing ever decides whether to
 * paper over it for the screen; the screen still sees exactly what it saw before this change,
 * because observing here is a transparent pass-through (`observing-client.ts`'s own contract), so
 * storing's `inner.get()` still resolves or rejects with precisely what `renewing` produced.
 *
 * **`createOfflineGuardClient` joins OUTERMOST of all — outside `storing` too — and reads
 * `navigator.onLine` (`connectivity.ts`) on purpose, which is not the FR-003 question by itself —
 * FR-003 forbids deciding reachability from the device's own report ALONE, not consulting it at
 * all. `isAllowedStoredViewUrl` exempts exactly the reads that must attempt for real regardless —
 * home's and `/unsent`'s — so `core/reach/` still hears the genuine outcome of THOSE, through
 * `observing`, whenever they run. For every screen this cycle has not reached, this guard's own
 * refusal (see its own header) is the ONLY thing that can tell `core/reach/` the device just went
 * offline at all — without it, a screen with nothing allow-listed to attempt could refuse forever
 * while `core/reach/` stayed `reachable`, having never observed anything to the contrary, and FR-005
 * would have no indication left to show. `offline-guard.ts`'s own header has the full correction:
 * an earlier version of this file's comment claimed the guard's refusal could never reach
 * `core/reach/`, which was wrong and is why this note now says otherwise.
 */
setAppClient(createOfflineGuardClient(createStoringClient(createObservingClient(createRenewingClient(directClient())))));

/**
 * 021, ported at 034 T057/T058 — the service worker, where Angular had `provideServiceWorker`.
 *
 * **Registered for the PRODUCTION build only, and that is load-bearing rather than cautious.**
 * `playwright.config.ts` serves a development build, so all of the inherited spec files keep
 * running against exactly the bundle they ran against before — 021 SC-005 true by construction
 * rather than by inspection. `vite.config.ts` enforces the other half: no worker is even EMITTED
 * outside a production build, so a stray registration here would 404 rather than quietly succeed.
 *
 * The branch is explicit rather than hidden inside the registrar, because `noServiceWorker` is what
 * the update store must be handed when there is none — a store left unwired would default to the
 * same thing, but silently, and "no offer appears" is a symptom both states share.
 */
startUpdateWatch(environment.production ? registerServiceWorker('/sw.js') : noServiceWorker);

/**
 * 036 review item 3 — nothing wired the outbox's ambient triggers (the `online` event,
 * `visibilitychange`, the five-second beat, and the session becoming signed-in) to anything: an
 * item made offline was never sent again unless the practitioner captured a second time. Called
 * once, here, the same way `startUpdateWatch` is — before the first render, and unconditionally
 * rather than from a component, since it needs no lifecycle but the application's own.
 */
startOutboxWatch();

/**
 * 036 (issue 219) — the WARM half of recovering from `unconfirmed`: retries a renewal on
 * `core/reach/`'s shared beat (the `online` event, the document becoming visible, and its
 * five-second poll) whenever the session is `unconfirmed`. Called unconditionally, before the first
 * render, the same way `startOutboxWatch` is — it costs nothing while there is nothing to recover.
 */
startSessionRecovery();

/**
 * 036 (issue 222, FR-023) — fetch-ahead's own two triggers (the session becoming `signed-in`, and
 * the document becoming visible while `core/reach/` says `reachable`). Called before `restore()`,
 * the same ordering `startOutboxWatch()` and `startSessionRecovery()` already need and for the
 * identical reason: `onSignedIn`'s subscription has to be registered before `restore()` can
 * possibly flip the session to `signed-in`, or the very first edge into it is missed.
 */
startFetchAhead();

const container = document.getElementById('root');
if (!container) throw new Error('index.html has no #root element to mount into');

/**
 * RESTORE BEFORE THE FIRST RENDER. 005 FR-009: the guards are pure predicates because the session is
 * already settled by the time the first navigation happens — that is what removes the sign-in/guard
 * redirect loop, rather than each guard remembering to wait. Angular did this with an
 * APP_INITIALIZER; here it is an await before createRoot.
 *
 * 036 (issue 219, criterion 2): the render waits on `firstPaintReady()`, NOT on `restore()`'s own
 * promise. `restore()` still resolves only once the session it started is fully confirmed (its own
 * header explains why that contract stays as it is for every other caller); `firstPaintReady()`
 * settles the instant `status` first leaves `unknown`, which for a stored token and a paired
 * identity happens synchronously inside `restore()`, long before any request has been answered —
 * that is what lets the shell draw the practitioner's own screen without waiting on a stalled
 * connection (R-006's five-second limit is `restore()`'s own bound, not the first paint's).
 */
/**
 * Created ONCE, outside render. `<RouterProvider router={createBrowserRouter(routes)} />` builds a
 * brand-new router on every render pass, which re-enters initialisation mid-render and surfaces as
 * React error 311 — a component suspending in response to synchronous input — with React Router's
 * own "Unexpected Application Error!" screen in place of the application. It reads as a routing bug
 * and is a lifetime bug.
 */
const router = createBrowserRouter(routes);

void restore();
void firstPaintReady().then(() => {
  createRoot(container).render(
    <StrictMode>
      {/* Outside the router, so every route — the anonymous ones included — wears the palette. */}
      <ThemeProvider theme={theme}>
        <RouterProvider router={router} />
      </ThemeProvider>
    </StrictMode>,
  );
});
